import React, { useState, useEffect, useRef } from 'react';
import { fetchTestimonials } from '../api';
import { useTranslation } from 'react-i18next';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL!;

interface Testimonial {
  _id: string;
  name: { en: string; ar: string };
  company: { en: string; ar: string };
  description: { en: string; ar: string };
  image: string;
  rating: number;
  isActive: boolean;
  priority: number;
}

interface SelectedReview {
  stars: number;
  text: string;
  name: string;
  image: string;
}

const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
  return (
    <div className="star-rating">
      {[...Array(5)].map((_, index) => (
        <span
          key={index}
          style={{
            color: index < rating ? '#b22222' : '#e4e5e9',
            fontSize: '18px',
          }}
        >
          ★
        </span>
      ))}
    </div>
  );
};

const Testimonials: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [selectedReview, setSelectedReview] = useState<SelectedReview | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();

  // Fetch testimonials on component mount
  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const data = await fetchTestimonials();
        // Filter active testimonials and sort by priority
        const activeTestimonials = data.testimonials
          .filter((t: Testimonial) => t.isActive)
          .sort((a: Testimonial, b: Testimonial) => a.priority - b.priority);
        setTestimonials(activeTestimonials);
        // Set initial review if available
        if (activeTestimonials.length > 0) {
          setSelectedReview({
            stars: activeTestimonials[0].rating,
            text:
              i18n.language === 'ar'
                ? activeTestimonials[0].description.ar
                : activeTestimonials[0].description.en,
            name:
              i18n.language === 'ar'
                ? activeTestimonials[0].name.ar
                : activeTestimonials[0].name.en,
            image: `${IMAGE_BASE_URL}/${activeTestimonials[0].image}`,
          });
        }
        setLoading(false);
      } catch (err) {
        setError(t('errors.fetchTestimonials'));
        setLoading(false);
      }
    };
    loadTestimonials();
  }, [t, i18n.language]);

  const handleProfileClick = (testimonial: Testimonial) => {
    setSelectedReview({
      stars: testimonial.rating,
      text:
        i18n.language === 'ar'
          ? testimonial.description.ar
          : testimonial.description.en,
      name: i18n.language === 'ar' ? testimonial.name.ar : testimonial.name.en,
      image: `${IMAGE_BASE_URL}/${testimonial.image}`,
    });
  };

  const handlePrev = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollTop -= 80;
    }
  };

  const handleNext = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollTop += 80;
    }
  };

  if (loading) {
    return <div>{t('loading')}</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (testimonials.length === 0 || !selectedReview) {
    return <div>{t('noTestimonials')}</div>;
  }

  return (
    <section className="testimonials bg-gray-100 py-10">
      <div className="container mx-auto px-4">
        <div className="row">
          <div className="col-md-12 text-center mb-8">
            <div className="content_wrapper">
              <h2>{t('testimonials')}</h2>
            </div>
          </div>
        </div>

        <div className="row py-5">
          <div className="col-md-7">
            <div
              className="box_wrapper"
              style={{
                height: '230px',
                padding: '16px',
                border: '1px solid #eee',
                borderRadius: '10px',
                backgroundColor: '#082341',
                color: '#fff',
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
              }}
            >
              <StarRating rating={selectedReview.stars} />
              <div className="description mt-3">
                <p
                  className="text-sm"
                  style={{
                    textAlign: i18n.language === 'ar' ? 'right' : 'left',
                  }}
                >
                  {selectedReview.text}
                </p>
              </div>
              <div
                className="user_info"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginTop: '10px',
                  flexDirection: 'row',
                }}
              >
                <img
                  src={selectedReview.image}
                  className="img-fluid"
                  alt={t('reviewer')}
                  style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                />
                <div className="avtar_info">
                  <p
                    style={{
                      margin: 0,
                      fontSize: '14px',
                      textAlign: i18n.language === 'ar' ? 'right' : 'left',
                    }}
                  >
                    {selectedReview.name}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <article className="total_person">
              <div className="slider vertical-slider relative">
                <button
                  className="custom-prev absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-200 hover:bg-gray-300 rounded-full w-8 h-8 flex items-center justify-center"
                  onClick={handlePrev}
                >
                  ‹
                </button>

                <div
                  className="slick"
                  ref={sliderRef}
                  style={{
                    maxHeight: '230px',
                    overflowY: 'auto',
                    scrollBehavior: 'smooth',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                >
                  <style>{`.slick::-webkit-scrollbar { display: none; }`}</style>
                  {testimonials.map((testimonial, index) => (
                    <div
                      className="item"
                      key={testimonial._id}
                      style={{
                        cursor: 'pointer',
                        height: '70px',
                        marginBottom: '8px',
                      }}
                    >
                      <div
                        className="profile-card"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: '#f8f8f8',
                          borderRadius: '8px',
                          padding: '8px',
                          width: '100%',
                          height: '100%',
                          flexDirection:
                            i18n.language === 'ar' ? 'row-reverse' : 'row',
                        }}
                      >
                        <img
                          src={`${IMAGE_BASE_URL}/${testimonial.image}`}
                          alt={
                            i18n.language === 'ar'
                              ? testimonial.name.ar
                              : testimonial.name.en
                          }
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                        <div className="profile-info">
                          <p
                            style={{
                              margin: 0,
                              fontWeight: 'bold',
                              fontSize: '14px',
                              textAlign:
                                i18n.language === 'ar' ? 'right' : 'left',
                            }}
                          >
                            {i18n.language === 'ar'
                              ? testimonial.name.ar
                              : testimonial.name.en}
                          </p>

                          <p
                            style={{
                              margin: 0,
                              fontSize: '12px',
                              color: '#666',
                              textAlign:
                                i18n.language === 'ar' ? 'right' : 'left',
                            }}
                          >
                            {i18n.language === 'ar'
                              ? testimonial.company.ar
                              : testimonial.company.en}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  className="custom-next absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-200 hover:bg-gray-300 rounded-full w-8 h-8 flex items-center justify-center"
                  onClick={handleNext}
                >
                  ›
                </button>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
